import { useEffect, useMemo, useState } from "react";
import { redistributeCaloriesEvenly } from "../../domain/planning/redistribution";
import type { AppController, AppSnapshot, ExecuteAction } from "../types";

interface WeekViewProps {
  snapshot: AppSnapshot;
  controller: AppController;
  execute: ExecuteAction;
}

interface AllocationDraft {
  readonly date: string;
  readonly calories: number;
  readonly minimumCalories: number;
  readonly isLocked: boolean;
  readonly inputValue: string;
}

export function WeekView({ snapshot, controller, execute }: WeekViewProps) {
  const { week } = snapshot;
  const [drafts, setDrafts] = useState<AllocationDraft[]>(() => fromWeek(snapshot));
  const [allocationError, setAllocationError] = useState<string>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDrafts(fromWeek(snapshot));
    setAllocationError(undefined);
  }, [snapshot.week]);

  const hasValidAllocationTotal = useMemo(
    () => drafts.reduce((sum, draft) => sum + draft.calories, 0) === week.weeklyBudgetCalories,
    [drafts, week.weeklyBudgetCalories],
  );
  const orderedDays = useMemo(
    () => [...week.days].sort((left, right) => left.date.localeCompare(right.date)),
    [week.days],
  );
  const chartMaximumCalories = useMemo(
    () => Math.max(1, ...drafts.map((draft) => draft.calories)),
    [drafts],
  );
  const chartMiddleCalories = Math.round(chartMaximumCalories / 2);
  const valid = hasValidAllocationTotal && drafts.every((draft) => {
    const inputCalories = Number(draft.inputValue);
    return draft.calories >= draft.minimumCalories
      && Number.isSafeInteger(inputCalories)
      && inputCalories >= draft.minimumCalories;
  });
  const unlockedCount = drafts.filter((draft) => !draft.isLocked).length;

  function updateInputValue(date: string, inputValue: string) {
    setAllocationError(undefined);
    setDrafts((current) => current.map((draft) => (
      draft.date === date ? { ...draft, inputValue } : draft
    )));
  }

  function commitDay(date: string) {
    const edited = drafts.find((draft) => draft.date === date);
    if (!edited || edited.inputValue === String(edited.calories)) return;

    const requestedCalories = Number(edited.inputValue);
    if (!Number.isSafeInteger(requestedCalories)) {
      resetVisibleInput(date);
      setAllocationError("Enter a whole calorie amount.");
      return;
    }

    try {
      const redistributed = redistributeCaloriesEvenly(drafts, date, requestedCalories);
      setDrafts(redistributed.map((draft) => ({ ...draft, inputValue: String(draft.calories) })));
      setAllocationError(undefined);
    } catch (error) {
      resetVisibleInput(date);
      setAllocationError(editErrorMessage(error, edited));
    }
  }

  function resetVisibleInput(date: string) {
    setDrafts((current) => current.map((draft) => (
      draft.date === date ? { ...draft, inputValue: String(draft.calories) } : draft
    )));
  }

  function toggleLocked(date: string) {
    setAllocationError(undefined);
    setDrafts((current) => current.map((draft) => (
      draft.date === date ? { ...draft, isLocked: !draft.isLocked } : draft
    )));
  }

  function resetAllocations() {
    setAllocationError(undefined);
    setDrafts(week.days.map((day) => ({
      date: day.date,
      calories: week.baselineDailyCalories,
      minimumCalories: day.dailyFloorCalories,
      isLocked: false,
      inputValue: String(week.baselineDailyCalories),
    })));
  }

  async function save() {
    if (saving) return;
    let committed = drafts;
    const pendingEdits = drafts.filter((draft) => draft.inputValue !== String(draft.calories));
    for (const pending of pendingEdits) {
      const requestedCalories = Number(pending.inputValue);
      if (!Number.isSafeInteger(requestedCalories)) {
        resetVisibleInput(pending.date);
        setAllocationError("Enter a whole calorie amount.");
        return;
      }
      try {
        committed = redistributeCaloriesEvenly(committed, pending.date, requestedCalories);
      } catch (error) {
        resetVisibleInput(pending.date);
        setAllocationError(editErrorMessage(error, pending));
        return;
      }
    }
    committed = committed.map((draft) => ({ ...draft, inputValue: String(draft.calories) }));
    const committedTotal = committed.reduce((sum, draft) => sum + draft.calories, 0);
    if (committedTotal !== week.weeklyBudgetCalories || committed.some((draft) => draft.calories < draft.minimumCalories)) {
      setAllocationError("The week must stay balanced and every day must remain above its calorie floor.");
      return;
    }

    setDrafts(committed);
    setAllocationError(undefined);
    setSaving(true);
    await execute(
      () => controller.saveWeekAllocations(committed.map(({ date, calories, isLocked }) => ({ date, calories, isLocked }))),
      { successMessage: "This week’s calorie allocations were saved." },
    );
    setSaving(false);
  }

  return (
    <section className="page-section" aria-labelledby="week-heading">
      <header className="page-heading">
        <div>
          <p className="eyebrow">{formatDateRange(week.startsOn, week.endsOn)}</p>
          <h1 id="week-heading">Your week</h1>
        </div>
        <button className="button button--ghost button--small" type="button" onClick={resetAllocations}>Reset allocations</button>
      </header>

      <div className="notice notice--info">
        <div>
          <strong>Flexible calories, fixed weekly budget</strong>
          <p>Edit an unlocked day and the other unlocked days share the difference evenly. Locked days stay fixed.</p>
        </div>
      </div>

      <article className="card allocation-card">
        <div className="card__heading">
          <div><h2>Daily allocations</h2><p>Macros update with each calorie allowance</p></div>
        </div>
        <ul className="allocation-macro-key" aria-label="Macronutrient chart key">
          <li><i className="allocation-macro-key__protein" aria-hidden="true" />Protein</li>
          <li><i className="allocation-macro-key__carbs" aria-hidden="true" />Carbs</li>
          <li><i className="allocation-macro-key__fat" aria-hidden="true" />Fat</li>
        </ul>
        <p className="allocation-card__scroll-hint" id="allocation-scroll-hint">Swipe sideways to compare all seven days.</p>
        <div className="allocation-chart-layout">
          <div
            className="allocation-chart-axis"
            role="img"
            aria-label={`Calorie chart scale: midpoint ${chartMiddleCalories.toLocaleString()} kcal; maximum ${chartMaximumCalories.toLocaleString()} kcal`}
          >
            <span className="allocation-chart-axis__tick allocation-chart-axis__tick--maximum" aria-hidden="true">
              {chartMaximumCalories.toLocaleString()}
            </span>
            <span className="allocation-chart-axis__tick allocation-chart-axis__tick--middle" aria-hidden="true">
              {chartMiddleCalories.toLocaleString()}
            </span>
          </div>
          <div
            className="allocation-week-scroll"
            role="region"
            aria-label="Seven-day calorie and macro allocations"
            aria-describedby="allocation-scroll-hint"
            tabIndex={0}
          >
            <ul className="allocation-week">
            {orderedDays.map((day) => {
              const draft = drafts.find((candidate) => candidate.date === day.date) ?? {
                date: day.date,
                calories: day.caloriesTarget,
                minimumCalories: day.dailyFloorCalories,
                isLocked: day.isLocked,
                inputValue: String(day.caloriesTarget),
              };
              const macros = calculateMacroTargets(
                draft.calories,
                week.fixedDailyProteinGrams,
                week.carbohydrateShareOfRemainder,
                week.fatShareOfRemainder,
              );
              const cannotBalance = !draft.isLocked && unlockedCount < 2;
              const inputDescription = cannotBalance
                ? `floor-${day.date} balance-${day.date}`
                : `floor-${day.date}`;
              return (
                <li
                  className={`allocation-day ${day.isToday ? "allocation-day--today" : ""} ${draft.isLocked ? "allocation-day--locked" : ""}`}
                  key={day.date}
                >
                  <div className="allocation-day__content" role="group" aria-labelledby={`allocation-day-${day.date}`}>
                    <header className="allocation-day__header">
                      <span className="allocation-day__date" id={`allocation-day-${day.date}`}>
                        <strong className="allocation-day__weekday" aria-hidden="true">{formatWeekdayInitial(day.date)}</strong>
                        <span className="sr-only">{day.longLabel}</span>
                      </span>
                      <button
                        className="allocation-day__lock"
                        type="button"
                        aria-label={`${draft.isLocked ? "Unlock" : "Lock"} ${day.longLabel}`}
                        aria-pressed={draft.isLocked}
                        onClick={() => toggleLocked(day.date)}
                      >
                        <span className={`padlock-icon ${draft.isLocked ? "padlock-icon--locked" : "padlock-icon--unlocked"}`} aria-hidden="true" />
                      </button>
                    </header>

                    <div
                      className="allocation-day__macro-chart"
                      role="img"
                      aria-label={`${day.longLabel} macros: Protein ${macros.protein} grams, Carbs ${macros.carbohydrates} grams, Fat ${macros.fat} grams`}
                    >
                      <div
                        className="allocation-day__macro-bar"
                        style={{ height: `${(draft.calories / chartMaximumCalories) * 100}%` }}
                        aria-hidden="true"
                      >
                        <span className="allocation-day__macro-segment allocation-day__macro-segment--protein" style={{ flexGrow: macros.protein * 4 }} />
                        <span className="allocation-day__macro-segment allocation-day__macro-segment--carbs" style={{ flexGrow: macros.carbohydrates * 4 }} />
                        <span className="allocation-day__macro-segment allocation-day__macro-segment--fat" style={{ flexGrow: macros.fat * 9 }} />
                      </div>
                    </div>

                    <div className="allocation-day__calories">
                      <label className="sr-only" htmlFor={`calories-${day.date}`}>{day.longLabel} calorie allowance</label>
                      <input
                        id={`calories-${day.date}`}
                        type="number"
                        inputMode="numeric"
                        min={draft.minimumCalories}
                        step="1"
                        value={draft.inputValue}
                        disabled={draft.isLocked || cannotBalance}
                        onChange={(event) => updateInputValue(day.date, event.target.value)}
                        onBlur={() => commitDay(day.date)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") event.currentTarget.blur();
                        }}
                        aria-describedby={inputDescription}
                      />
                      <span aria-hidden="true">kcal</span>
                    </div>
                    <small className="allocation-day__floor" id={`floor-${day.date}`}>min {draft.minimumCalories.toLocaleString()}</small>
                    {cannotBalance && <span className="sr-only" id={`balance-${day.date}`}>Unlock another day to change this allowance.</span>}
                  </div>
                </li>
              );
            })}
            </ul>
          </div>
        </div>

        <div className="allocation-card__footer">
          {allocationError && <p className="form-error" role="alert">{allocationError}</p>}
          <button className="button button--primary button--full" type="button" disabled={!valid || saving} onClick={save}>
            {saving ? "Saving…" : "Save week"}
          </button>
        </div>
      </article>
    </section>
  );
}

function fromWeek(snapshot: AppSnapshot): AllocationDraft[] {
  return snapshot.week.days.map((day) => ({
    date: day.date,
    calories: day.caloriesTarget,
    minimumCalories: day.dailyFloorCalories,
    isLocked: day.isLocked,
    inputValue: String(day.caloriesTarget),
  }));
}

function calculateMacroTargets(
  calories: number,
  proteinGrams: number,
  carbohydrateShare: number,
  fatShare: number,
) {
  const remainingCalories = Math.max(0, calories - proteinGrams * 4);
  return {
    protein: Math.round(proteinGrams),
    carbohydrates: Math.round((remainingCalories * carbohydrateShare) / 4),
    fat: Math.round((remainingCalories * fatShare) / 9),
  };
}

function editErrorMessage(error: unknown, edited: AllocationDraft) {
  const message = error instanceof Error ? error.message : "";
  const normalizedMessage = message.toLowerCase();
  if (normalizedMessage.includes("floor")) {
    return `${formatDayDate(edited.date)} must stay at or above ${edited.minimumCalories.toLocaleString()} kcal.`;
  }
  if (normalizedMessage.includes("unlock another day")) return "Unlock another day before changing this allowance.";
  if (normalizedMessage.includes("support at most")) {
    return "That change would take another unlocked day below its calorie floor.";
  }
  return "That calorie change could not be shared across the unlocked days.";
}

function formatDayDate(date: string) {
  return safeDate(date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

function formatWeekdayInitial(date: string) {
  return safeDate(date).toLocaleDateString(undefined, { weekday: "narrow" });
}

function formatDateRange(start: string, end: string) {
  const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  return `${safeDate(start).toLocaleDateString(undefined, options)} – ${safeDate(end).toLocaleDateString(undefined, options)}`;
}

function safeDate(date: string) {
  return new Date(`${date}T12:00:00`);
}
